import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import type { Reference } from '@apollo/client';
import { Apollo } from 'src/gql/generated';

/** Creates a category in a playbook. */
export function useCreatePlaybookRuleCategory() {
  const [createPlaybookRuleCategory, { loading }] = Apollo.useCreatePlaybookRuleCategoryMutation();
  const safeCreatePlaybookRuleCategory = useCallback(
    (options: { name: string; playbookId: UUID }) => {
      const { name, playbookId } = options;

      return createPlaybookRuleCategory({
        update(cache, { data }) {
          if (!data?.createPlaybookRuleCategory) return;

          const newCategoryRef = cache.writeFragment({
            /**
             * With an empty rules, which a new category always has. The
             * settings screens select that field, so writing the category
             * without it would leave their query unable to read the row it
             * just added — and the first rule appended to it nowhere to go.
             */
            data: { ...data.createPlaybookRuleCategory, rules: [] },
            fragment: Apollo.PlaybookRuleCategoryWithRulesFragmentDoc,
            fragmentName: Apollo.namedOperations.Fragment.PlaybookRuleCategoryWithRules,
          });
          if (!newCategoryRef) return;

          cache.modify({
            fields: {
              categories: (refs: readonly Reference[] = [], { readField }) => {
                // Guards against the same response being applied twice, e.g. a
                // refetch racing this update — not against a name collision,
                // which is allowed.
                const categoryId = data.createPlaybookRuleCategory.id;
                if (refs.some(ref => readField<UUID>('id', ref) === categoryId)) return refs;

                return [...refs, newCategoryRef];
              },
            },
            id: cache.identify({ __typename: 'Playbook', id: playbookId }),
          });
        },
        variables: { input: { name, playbookId } },
      });
    },
    [createPlaybookRuleCategory]
  );

  return { createPlaybookRuleCategory: safeCreatePlaybookRuleCategory, loading };
}

/**
 * Renames a category. The mutation returns the updated category, normalized by
 * id, so no manual cache write is needed.
 */
export function useUpdatePlaybookRuleCategory() {
  const [updatePlaybookRuleCategory, { loading }] = Apollo.useUpdatePlaybookRuleCategoryMutation();
  const safeUpdatePlaybookRuleCategory = useCallback(
    (input: Apollo.UpdatePlaybookRuleCategoryInput) => {
      return updatePlaybookRuleCategory({ variables: { input } });
    },
    [updatePlaybookRuleCategory]
  );

  return { loading, updatePlaybookRuleCategory: safeUpdatePlaybookRuleCategory };
}

/**
 * Moves a category within its playbook.
 *
 * The cached `categories` list holds the server's order and nothing re-sorts
 * it by rank on the client, so the returned category — which only updates its
 * own fields — is not enough: the category is moved to `toIndex` by hand.
 * `toIndex` counts among the playbook's *other* categories, which is why the
 * category is taken out before the slot is counted.
 *
 * Pass `optimisticCategory` (the category as it will read after the move,
 * including the rank it is expected to get) to apply that move straight away,
 * so a dropped card stays where it was dropped instead of snapping back until
 * the server replies. The server's reply then replaces the guessed rank.
 */
export function useMovePlaybookRuleCategory() {
  const [movePlaybookRuleCategory, { loading }] = Apollo.useMovePlaybookRuleCategoryMutation();
  const safeMovePlaybookRuleCategory = useCallback(
    (options: {
      input: Apollo.MovePlaybookRuleCategoryInput;
      playbookId: UUID;
      optimisticCategory?: Apollo.PlaybookRuleCategorySimpleFragment;
    }) => {
      const { input, optimisticCategory, playbookId } = options;
      const { categoryId, toIndex } = input;

      return movePlaybookRuleCategory({
        optimisticResponse: optimisticCategory
          ? { __typename: 'Mutation', movePlaybookRuleCategory: optimisticCategory }
          : undefined,
        update(cache, { data }) {
          if (!data?.movePlaybookRuleCategory) return;

          cache.modify({
            fields: {
              categories: (refs: readonly Reference[] = [], { readField }) => {
                const moved = refs.find(ref => readField<UUID>('id', ref) === categoryId);
                if (!moved) return refs;

                const others = refs.filter(ref => ref !== moved);
                const index = Math.max(0, Math.min(toIndex, others.length));

                return [...others.slice(0, index), moved, ...others.slice(index)];
              },
            },
            id: cache.identify({ __typename: 'Playbook', id: playbookId }),
          });
        },
        variables: { input },
      });
    },
    [movePlaybookRuleCategory]
  );

  return { loading, movePlaybookRuleCategory: safeMovePlaybookRuleCategory };
}

/**
 * Deletes a category and its rules from a playbook.
 *
 * No cache surgery: the mutation returns the playbook, so Apollo renormalizes
 * both lists from the response. Nothing is renumbered on the category side —
 * the survivors keep the ranks they had, since a rank leaves no gap to close.
 * The rules are what makes hand-filtering wrong: the delete cascades into every
 * rule the category owned, so removing just the category from the list would
 * leave all of them behind in the cache.
 */
export function useDeletePlaybookRuleCategory() {
  const [deletePlaybookRuleCategory, { loading }] = Apollo.useDeletePlaybookRuleCategoryMutation();
  const safeDeletePlaybookRuleCategory = useCallback(
    (options: { categoryId: UUID }) => {
      return deletePlaybookRuleCategory({ variables: { categoryId: options.categoryId } });
    },
    [deletePlaybookRuleCategory]
  );

  return { deletePlaybookRuleCategory: safeDeletePlaybookRuleCategory, loading };
}
